import useUserInfo from '../../../hooks/useUserInfo';
import { useSelector } from 'react-redux';
import { selectUserInfoData } from '../../../reducers/userInfoReducer';
import { AccountType } from '@server/account-type';
import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';
import useSendBiEvent from '../../../hooks/useSendBiEvent';
import { PAYWALL_EVENTS } from '../../../services/events';
import { ActionPaywallSource, PaywallBlockProps, PaywallState } from '../paywall/paywallTypes';
import { useLocation } from 'react-router';
import { ACTIVATE_TRIAL_COOKIE_NAME } from './paywallConsts';

const ACTIVE_PAYWALL_STATES = [PaywallState.TRIAL_EXPIRED, PaywallState.PRE_TRIAL];

const getPaywallState = (type?: AccountType, trialEndDate?: string) => {
  switch (type) {
    case AccountType.Customer:
    case AccountType.Internal:
    case AccountType.POC:
      return PaywallState.CUSTOMER;
    case AccountType.Inactive:
      return PaywallState.TRIAL_EXPIRED;
    case AccountType.Free:
    case AccountType.SI:
      if (!trialEndDate) {
        return PaywallState.PRE_TRIAL;
      }
      const trialEnd = DateTime.fromISO(trialEndDate);
      const now = DateTime.fromISO(new Date().toISOString());
      if (trialEnd >= now) {
        return PaywallState.IN_TRIAL;
      } else {
        return PaywallState.TRIAL_EXPIRED;
      }
    default:
      console.error('Unknown account type:', type);
      return PaywallState.TRIAL_EXPIRED;
  }
};

const calcDaysLeft = (trialEndDate: string) => {
  const trialEnd = DateTime.fromISO(trialEndDate);
  const now = DateTime.now();
  const diff = trialEnd.diff(now, ['days']).toObject();
  return Math.ceil(diff?.days ?? 0);
};

const usePaywall = () => {
  const { startTrial } = useUserInfo();
  const user = useSelector(selectUserInfoData);
  const { trialEndDate, type } = user?.account ?? {};
  const sendBiEvent = useSendBiEvent();
  const { pathname } = useLocation();
  const mainPath = pathname.split('/')[1];

  const paywallState = useMemo(() => {
    return getPaywallState(type, trialEndDate);
  }, [trialEndDate, type]);

  const daysLeft = useMemo(() => {
    return paywallState === PaywallState.IN_TRIAL && trialEndDate
      ? calcDaysLeft(trialEndDate)
      : null;
  }, [paywallState, trialEndDate]);

  const isPaywallActive = useMemo(
    () => ACTIVE_PAYWALL_STATES.includes(paywallState),
    [paywallState],
  );

  const sendTrialActivateEvent = useCallback(
    (source: ActionPaywallSource | string) => {
      sendBiEvent({
        name: PAYWALL_EVENTS.trialActivated,
        props: {
          source,
        },
      });
    },
    [sendBiEvent],
  );

  const removeTrialCookie = useCallback(() => {
    document.cookie =
      ACTIVATE_TRIAL_COOKIE_NAME + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
  }, []);

  const sendEncounterPaywallEvent = useCallback(
    (paywallBlockProps?: PaywallBlockProps) => {
      const { source, objectName } = paywallBlockProps ?? {};
      sendBiEvent({
        name: PAYWALL_EVENTS.paywall,
        props: {
          trialStatus: paywallState,
          objectName,
          source: source ?? mainPath,
        },
      });
    },
    [paywallState, sendBiEvent, mainPath],
  );

  const startTrialWithEvent = useCallback(
    async (source: ActionPaywallSource) => {
      await startTrial();
      sendTrialActivateEvent(source);
      removeTrialCookie();
    },
    [startTrial, sendTrialActivateEvent, removeTrialCookie],
  );

  return {
    startTrial: startTrialWithEvent,
    daysLeft,
    isPaywallActive,
    sendEncounterPaywallEvent,
    paywallState,
  };
};

export default usePaywall;
